import { useState, useEffect } from "react";
import { useTranslation } from 'react-i18next';
import UserManagement from "./UserManagement";
import ParkingManagement from "./ParkingManagement";
import RegistrationRequestsManagement from "./RegistrationRequestsManagement";
import { Card } from '../ui/card';
import { Users, Car, Building, UserCheck } from 'lucide-react';
import useUser from '../../api/user';

const Management = () => {
    const { t } = useTranslation();
    const [activeTab, setActiveTab] = useState("users");
    const [pendingRequests, setPendingRequests] = useState(0);
    const userApi = useUser();
    
    // Define filter categories with translations
    const FILTER_CATEGORIES = [
      { id: 'users', label: t('management.categories.users'), icon: Users },
      { id: 'registrations', label: t('management.categories.registrations'), icon: UserCheck, hasBadge: true },
      { id: 'parkingSpots', label: t('management.categories.parkingSpots'), icon: Car },
      { id: 'building', label: t('management.categories.building'), icon: Building },
    ];
    
    // Fetch pending registration requests count
    useEffect(() => {
        const fetchPendingRequestsCount = async () => {
            try {
                const users = await userApi.getAllUsers();
                console.log("Fetched users:", users);
                const pendingCount = users.filter(user => 
                    user.status == '0' && user.accepted == '0'
                ).length;
                setPendingRequests(pendingCount);
            } catch (error) {
                console.error(t('management.errors.fetchRequests'), error);
            }
        };
        
        fetchPendingRequestsCount();
    }, []);

    const renderActiveTab = () => {
        switch (activeTab) {
            case "users":
                return <UserManagement />;
            case "registrations":
                return <RegistrationRequestsManagement onRequestProcessed={() => {
                    // Decrement badge count when a request is processed
                    setPendingRequests(prev => Math.max(0, prev - 1));
                }} />;
            case "parkingSpots":
                return <ParkingManagement />;
            case "building":
                return (
                    <div className="flex flex-col items-center justify-center p-12 text-gray-500 h-64">
                        <Building className="w-12 h-12 mb-4 opacity-30" />
                        <p className="text-lg font-medium">{t('management.building.title')}</p>
                        <p className="text-sm">{t('management.building.comingSoon')}</p>
                    </div>
                );
            default:
                return <UserManagement />;
        }
    };

    return (
        <div className="p-4 md:p-6 space-y-4 max-h-[calc(100dvh)] md:space-y-6 pb-20 md:pb-6 overflow-y-auto h-full">
            {/* Header */}
            <div className="flex flex-col space-y-2">
                <h1 className="text-2xl md:text-3xl font-bold">{t('management.title')}</h1>
                <p className="text-sm text-muted-foreground">{t('management.description')}</p>
            </div>
            
            {/* Filter Tabs - Horizontally scrollable on mobile */}
            <Card className="p-3 bg-white dark:bg-neutral-900 z-0">
                <div className="flex overflow-x-auto no-scrollbar pb-1">
                    {FILTER_CATEGORIES.map((category) => (
                        <button
                            key={category.id}
                            onClick={() => setActiveTab(category.id)}
                            className={`flex items-center gap-2 px-3 py-2 rounded-lg whitespace-nowrap z-0 mr-2 min-w-fit relative ${
                                activeTab === category.id
                                ? 'bg-primary text-white'
                                : 'bg-neutral-100 dark:bg-neutral-800 hover:bg-neutral-200 dark:hover:bg-neutral-700'
                            } transition-colors`}
                        >
                            <category.icon className="w-4 h-4" />
                            <span className="text-sm font-medium">{category.label}</span>
                            {category.hasBadge && pendingRequests > 0 && (
                                <span className="absolute -top-1 -right-1 flex items-center justify-center w-5 h-5 text-xs font-bold text-white bg-red-500 rounded-full z-10">
                                    {pendingRequests}
                                </span>
                            )}
                        </button>
                    ))}
                </div>
            </Card>
            
            {/* Content Area */}
            <div className="bg-white dark:bg-neutral-900 rounded-lg shadow">
                {renderActiveTab()}
            </div>
        </div>
    );
};

export default Management;